import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface ModalButton {
  text: string;
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';
  variantCancel?: 'secondary' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
  disabled?: boolean;
  loading?: boolean;
  action: string; // Identificador único del botón
}

@Component({
  selector: 'app-custom-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './custom-modal.html',
  styleUrls: ['./custom-modal.css']
})
export class CustomModalComponent implements OnInit, OnDestroy {
  @Input() isOpen: boolean = false;
  @Input() title: string = '';
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' = 'md';
  @Input() showCloseButton: boolean = true;
  @Input() closeOnBackdrop: boolean = true;
  @Input() closeOnEscape: boolean = true;

  // Botones del modal
  @Input() buttons: ModalButton[] = [];
  @Input() showCancelButton: boolean = true;
  @Input() cancelButtonText: string = 'Cancelar';

  // Contenido del modal
  @Input() content: string = ''; // HTML string para contenido simple

  // Eventos
  @Output() close = new EventEmitter<void>();
  @Output() buttonClick = new EventEmitter<string>(); // Emite el action del botón
  @Output() cancel = new EventEmitter<void>();

  ngOnInit() {
    if (this.isOpen) {
      this.handleModalOpen();
    }
  }

  ngOnDestroy() {
    this.handleModalClose();
  }

  ngOnChanges() {
    if (this.isOpen) {
      this.handleModalOpen();
    } else {
      this.handleModalClose();
    }
  }

  private handleModalOpen() {
    // Prevenir scroll del body cuando el modal está abierto
    document.body.style.overflow = 'hidden';

    // Agregar listener para ESC
    if (this.closeOnEscape) {
      document.addEventListener('keydown', this.handleEscapeKey.bind(this));
    }
  }

  private handleModalClose() {
    // Restaurar scroll del body
    document.body.style.overflow = 'auto';

    // Remover listener para ESC
    document.removeEventListener('keydown', this.handleEscapeKey.bind(this));
  }

  private handleEscapeKey(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closeModal();
    }
  }

  closeModal() {
    this.close.emit();
  }

  onBackdropClick(event: Event) {
    if (this.closeOnBackdrop && event.target === event.currentTarget) {
      this.closeModal();
    }
  }

  onButtonClick(button: ModalButton) {
    if (!button.disabled && !button.loading) {
      this.buttonClick.emit(button.action);
    }
  }

  onCancelClick() {
    this.cancel.emit();
    this.closeModal();
  }

  get modalSizeClass(): string {
    const sizeClasses = {
      sm: 'modal-sm',
      md: 'modal-md',
      lg: 'modal-lg',
      xl: 'modal-xl'
    };
    return sizeClasses[this.size];
  }

  getButtonClass(button: ModalButton): string {
    const variant = button.variant || 'primary';
    let classes = `modal-button btn-${variant}`;

    if (button.disabled) classes += ' btn-disabled';
    if (button.loading) classes += ' btn-loading';

    return classes;
  }

  getButtonClassCancel(button: ModalButton): string {
    const variant = button.variantCancel || 'secondary';
    let classes = `modal-button btn-${variant}`;

    if (button.disabled) classes += ' btn-disabled';
    if (button.loading) classes += ' btn-loading';

    return classes;
  }
}
